import { LoadingOutlined } from '@ant-design/icons'
import { Empty, Pagination, Select } from 'antd'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'

import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import {
  getStudioList,
  getTeamByStudioGroup,
} from '../../api/aops.vacation.api'
import { userState } from '../../store/user'
import { formatLeave } from '../../utils/calcuateAnnualLeave'
import './vacationPage.scss'
const VacationEmployeesListTable = ({ shouldRefresh }) => {
  const [user] = useRecoilState(userState)
  const [employeeList, setEmployeeList] = useState([])
  const [studioList, setStudioList] = useState(null)
  const [selectedStudioGroupId, setSelectedStudioGroupId] = useState(0)
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(5)
  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(false)

  const fetchStudioList = async () => {
    const res = await getStudioList()
    const filteredStudioList = res?.filter((item) => item.id !== 1)
    setStudioList([
      { id: 0, name: 'ALL', nameKo: '전체' },
      ...filteredStudioList,
    ])
  }

  const fetchTeamByStudioGroup = async (studioGroupId, page = 0) => {
    setLoading(true)
    try {
      const res = await getTeamByStudioGroup({
        studioGroupId: studioGroupId === 0 ? null : studioGroupId,
        page,
        size: pageSize,
        sort: 'id,DESC',
      })
      setEmployeeList(res?.content || [])
      setPaginationInfo({
        totalElements: res?.totalElements || 0,
        totalPages: res?.totalPages || 0,
      })
    } catch (error) {
      console.error('Failed to fetch employee list:', error)
      setEmployeeList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user?.id) {
      fetchStudioList()
    }
  }, [user?.id, shouldRefresh])
  useEffect(() => {
    if (studioList && studioList.length > 0) {
      fetchTeamByStudioGroup(selectedStudioGroupId, currentPage - 1)
    }
  }, [selectedStudioGroupId, currentPage, studioList])

  const handleStudioGroupChange = (value) => {
    setSelectedStudioGroupId(value)
    setCurrentPage(1) // Reset to first page when changing studio group
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const getVacationStatus = (item) => {
    if (!item.hiredDate || !item.totalIssuedDays) {
      return ''
    }

    const today = dayjs()
    const hiredDate = dayjs(item.hiredDate)
    const expiryDate = hiredDate.add(1, 'year').subtract(1, 'day')

    // 전체 유효기간 (일 단위)
    const totalValidDays = expiryDate.diff(hiredDate, 'day') + 1

    // 경과 기간 (일 단위)
    const elapsedDays = today.diff(hiredDate, 'day') + 1

    // 남은 날 (일 단위)
    const remainingDays = expiryDate.diff(today, 'day')

    // 1. 초반 과다 사용 체크
    if (item.totalIssuedDays > 0 && totalValidDays > 0) {
      // 현재 소진률 = 사용연차 / 총 발생 연차
      const currentUsageRate = item.totalUsedDays / item.totalIssuedDays

      // 기대 소진률 = 경과 기간 / 전체 유효기간
      const expectedUsageRate = elapsedDays / totalValidDays

      // 초반 과다 사용: 현재 소진률 >= 기대 소진률 * 1.3
      if (currentUsageRate >= expectedUsageRate * 1.3) {
        return '초반 과다 사용'
      }
    }

    // 2. 촉진 필요 체크
    if (item.totalIssuedDays > 0) {
      // 남은 날 / 1년 ≤ 30%
      const remainingDaysRatio = remainingDays / 365

      // 잔여 연차 비율 = 잔여 연차 / 총 발생 연차
      const remainingVacationRatio = item.remainedDays / item.totalIssuedDays

      // 촉진 필요: 남은 날 / 1년 ≤ 30% AND 잔여 연차 비율 ≥ 50%
      if (remainingDaysRatio <= 0.3 && remainingVacationRatio >= 0.5) {
        return '촉진 필요'
      }
    }

    return ''
  }

  const returnMonthToYear = (month) => {
    // 12개월 미만일 때 (0~11개월)
    if (month < 12) {
      return `${month}개월`
    }

    const years = Math.floor(month / 12)
    const remainingMonths = month % 12

    // 12, 24, 36개월 등 딱 떨어지는 경우
    if (remainingMonths === 0) {
      return `${years}년 차`
    }

    // 그 외 (예: 1년 3개월 차)
    return `${years}년 ${remainingMonths}개월 `
  }

  return (
    <section className="vacation-box">
      <div className="vacation-history-header-wrapper">
        <div className="vacation-history-header">사용 연차 이력 확인</div>
      </div>
      <div className="table-wrapper">
        <table className="vacation-history-table">
          <thead className="detail-list-thead">
            <tr>
              <th>
                <div className="studio-group-selector">
                  <Select
                    className="form-select"
                    value={selectedStudioGroupId}
                    onChange={handleStudioGroupChange}
                    style={{ width: 200 }}
                    placeholder="스튜디오 그룹 선택"
                  >
                    {studioList?.map((studio) => (
                      <Select.Option key={studio.id} value={studio.id}>
                        {studio.nameKo}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </th>
              {/* <th>이름</th> */}
              <th>발급갯수 / 근속연수</th>
              <th>이번 달 사용</th>
              <th>누적 사용</th>
              <th>잔여 연차</th>
              <th>상태</th>
              <th>입사일</th>
              <th>만료일</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: 'center', padding: '40px' }}
                >
                  <LoadingOutlined />
                </td>
              </tr>
            ) : employeeList && employeeList.length > 0 ? (
              employeeList.map((item) => {
                return (
                  <tr
                    key={`${item.userId}-${item.name}-employee-vacation`}
                    onClick={() => {
                      navigate(`/hr/annual-leave/${item.userId}`)
                    }}
                  >
                    <td
                      className="employee-name"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        fontSize: 16,
                        fontWeight: 700,
                        color: '#191A1C',
                      }}
                    >
                      <span>{item.name}</span>
                      <span
                        style={{
                          display: 'flex',
                          gap: 4,
                          color: '#585F65',
                          fontSize: 14,
                          fontWeight: 400,
                        }}
                      >
                        <span>
                          {item.studioGroupName + '스튜디오' || '전체'}
                        </span>
                        <span>|</span>
                        <span>{item.teamGroupName || '-'}</span>
                      </span>
                    </td>
                    <td>
                      {item.totalIssuedDays}개 /{' '}
                      {returnMonthToYear(item.tenureYears)}
                    </td>
                    <td>{formatLeave(item.monthUsedDays)}</td>
                    <td>{formatLeave(item.totalUsedDays)}</td>
                    <td>{formatLeave(item.remainedDays)}</td>
                    <td>
                      <div
                        className={`table-tag-item ${getVacationStatus(item)?.includes('과다') ? 'urgent' : getVacationStatus(item)?.includes('촉진') ? 'required' : 'normal'}`}
                        style={{ padding: `4px 10px` }}
                      >
                        {getVacationStatus(item)}
                      </div>
                    </td>
                    <td>{dayjs(item.hiredDate).format('YY.MM.DD') || '-'}</td>
                    <td>
                      {dayjs(item.hiredDate)
                        .add(1, 'year')
                        .subtract(1, 'day')
                        .format('YY.MM.DD') || '-'}
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: 'center', padding: '40px' }}
                >
                  <Empty description="연차 이력이 없습니다." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {paginationInfo.totalElements > 0 && (
        <div className="pagination-wrapper">
          <Pagination
            current={currentPage}
            total={paginationInfo.totalElements}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper={false}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} / 총 ${total}개`
            }
          />
        </div>
      )}
    </section>
  )
}

export default VacationEmployeesListTable
